import React, { useEffect, useState } from "react"
import PubSub from "pubsub-js"
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Image from "aldoo-ra/Image"
import useT from "../hooks/useT"
import { usePageTitle } from "../hooks/usePageTitle"
import { SendFeedback } from "../storeApi"

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const inputBase =
  "w-full bg-transparent border-0 border-b py-2 text-[16px] text-text placeholder:text-text_secondary/70 outline-none focus:border-text transition-colors"

function Field({ label, error, children }) {
  return (
    <label className="flex flex-col gap-1 w-full">
      <span className="text-sm font-semibold text-text_secondary">
        {label} *
      </span>
      {children}
      {error && <span className="text-sm text-error">{error}</span>}
    </label>
  )
}

function SuccessPanel() {
  const t = useT()
  return (
    <div className="flex flex-col items-center justify-center gap-8 text-center max-w-[720px]" data-contact-sent>
      <Typography variant="h1">
        {t`Thank You! We've got your message. We'll get back to you shortly!`}
      </Typography>
      <Typography variant="body1_dim" className="my-0" role="status">
        {t`Your message was sent`}
      </Typography>
      <Image id="paper_plane" className="w-[200px] h-auto" />
      <Button variant="primary_l" href="/">
        {t`Return to Home`}
      </Button>
    </div>
  )
}

/**
 * /contact — port of the old Contact page: name / email / message form sent as
 * feedback {send}, plus a "Chat with us" shortcut.
 */
export default function ContactPage() {
  const t = useT()
  usePageTitle(t`Contact Us`)

  const [values, setValues] = useState({ name: "", email: "", message: "" })
  // the submit stays disabled until React is attached (no lost native submit)
  const [ready, setReady] = useState(false)
  useEffect(() => setReady(true), [])
  const [errors, setErrors] = useState({})
  const [waiting, setWaiting] = useState(false)
  const [failed, setFailed] = useState(false)
  const [sent, setSent] = useState(false)

  const update = (field) => (e) => {
    setValues((v) => ({ ...v, [field]: e.target.value }))
    setErrors((err) => ({ ...err, [field]: null }))
  }

  const submit = async (e) => {
    e.preventDefault()
    if (waiting) return
    const name = values.name.trim()
    const email = values.email.trim()
    const msg = values.message.trim()
    setFailed(false)

    const nextErrors = {}
    if (name.length < 3) nextErrors.name = t`Name should be at least 3 charcters`
    if (!EMAIL_RE.test(email)) nextErrors.email = t`The email appears to be invalid`
    if (msg.length < 10) nextErrors.message = t`The message should be at least 10 charcters`
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors)
      return
    }

    setWaiting(true)
    try {
      const response = await SendFeedback(`name: ${name}\nemail: ${email}\nmessage: ${msg}`)
      const ok = !!response?.result && !response?.error
      setFailed(!ok)
      setSent(ok)
    } catch {
      setFailed(true)
    }
    setWaiting(false)
  }

  if (sent) {
    return (
      <div className="w-full flex flex-col items-center px-5 sm:px-[60px] py-[50px] md:py-[100px]">
        <SuccessPanel />
      </div>
    )
  }

  return (
    <div className="w-full flex flex-col items-center px-5 sm:px-[60px] py-[50px] md:py-[100px]">
      <div className="w-full max-w-[800px] flex flex-col gap-10 items-center md:items-start">
        <Typography 
          variant="h1" 
          className="text-center sm:text-left"
        >
          {t`Let's Talk`}
        </Typography>

        <div className="w-full flex flex-col md:flex-row gap-10">
          <div className="flex flex-col items-center gap-6 shrink-0 md:mt-[30px]">
            <Image id="write_message" alt="write to us" className="w-[190px] h-[190px] rounded-full object-cover" />

            <div className="flex flex-col items-center gap-2 text-center text-[15px] text-text_secondary">
              <a href="mailto:support@aldoo.com" className="text-text font-semibold underline hover:text-primary transition-colors">
                support@aldoo.com
              </a>

              <Button 
                variant="secondary" 
                onClick={() => PubSub.publish("show-chat")}
              >
                <ChatBubbleLeftRightIcon className="w-5 h-5" />
                {t`Chat with us`}
              </Button>
            </div>
          </div>

          <form className="w-full max-w-[800px] md:max-w-[570px] flex flex-col gap-5 md:gap-6" onSubmit={submit} noValidate>
            <Field label={t`Name`} error={errors.name}>
              <input
                name="name"
                type="text"
                autoFocus
                required
                value={values.name}
                onChange={update("name")}
                className={`${inputBase} ${errors.name ? "border-error" : "border-bordered"}`}
              />
            </Field>
            <Field label={t`Email`} error={errors.email}>
              <input
                name="email"
                type="email"
                required
                value={values.email}
                onChange={update("email")}
                className={`${inputBase} ${errors.email ? "border-error" : "border-bordered"}`}
              />
            </Field>
            <Field label={t`Your Message`} error={errors.message}>
              <textarea
                name="message"
                rows={4}
                required
                value={values.message}
                onChange={update("message")}
                className={`${inputBase} resize-y min-h-[110px] ${errors.message ? "border-error" : "border-bordered"}`}
              />
            </Field>

            {failed && (
              <p className="m-0 text-error" role="alert">
                {t`Oops! There was an error sending your message. Please try again.`}
              </p>
            )}

            <div className="w-full flex justify-center md:justify-end mt-[10px]">
              <Button variant="primary_l" loading={waiting} disabled={!ready} className="w-full max-w-[300px]">
                {t`Submit`}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
